import { ReactNode } from 'react';
import { motion } from 'framer-motion';

interface GlassCardProps {
  children: ReactNode;
  className?: string;
  hover?: boolean;
  glow?: boolean;
  // 'inset' renders a pressed neomorphic well (empty states, sub-panels)
  // instead of a raised card. Overrides hover/glow.
  variant?: 'raised' | 'inset';
  accentColor?: string;
  onClick?: () => void;
  // When the card is interactive (onClick set), `ariaPressed` reflects its
  // toggle / selection state for AT users. Pure-click cards (e.g. nav tiles)
  // can leave it undefined.
  ariaPressed?: boolean;
}

const GlassCard = ({
  children,
  className = '',
  hover = true,
  glow = false,
  variant = 'raised',
  onClick,
  ariaPressed,
}: GlassCardProps) => {
  const surface =
    variant === 'inset'
      ? 'neo-inset'
      : glow
        ? 'card-glow'
        : hover
          ? 'card-elevated-hover'
          : 'card-elevated';
  // If a card has an onClick, it must be reachable by keyboard. Auto-inject
  // role/tabIndex/onKeyDown — Enter and Space both fire the click handler,
  // matching native <button> semantics. Cards without onClick stay
  // presentational (no role, not in tab order).
  const interactive = !!onClick;
  return (
    <motion.div
      className={`${surface} p-5 ${className}`}
      onClick={onClick}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      aria-pressed={interactive ? ariaPressed : undefined}
      // A pressed well never lifts — variant="inset" neutralizes hover even if
      // a call site forgets hover={false} (R2 reviewer caught Testing.tsx doing
      // exactly that; guarding here kills the whole bug class).
      whileHover={variant !== 'inset' && (hover || glow) ? { y: -3 } : undefined}
      transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
    >
      {children}
    </motion.div>
  );
};

export default GlassCard;
